import { selectLicenseProductState } from '../../../reducers/SmartDentReducer';
import { DEFAULT_LICENSE_PRODUCT } from '../../../actions/constants/LicenseProductConstant';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { SmartDentAction } from '../../../actions/SmartDentAction';
import { SmartDentConstant } from '../../../actions/constants/SmartDentConstant';
import { useLicenseProduct } from '../../../hooks/useLicenseProduct';
import { CompanyAction } from '../../../actions/CompanyAction';
import { computeDealerRemaining, computeNodeQuotaBreakdown, extractAllocationFacts } from '../../../lib/smartDentQuota';
import { buildParentOf } from '../../../lib/allocationContext';
import { getDirectAllocationChildren } from '../../../lib/allocationGraph';
import { ORG_SUBSIDIARY, ROLE_INTERNAL_DEALER, ROLE_EXTERNAL_DEALER } from '../../../config/appConstants';
import { Translate as t } from '../../../config/Translate';
import VoucherTemplateEditor from './VoucherTemplateEditor';
import DealerIssuePanel from './DealerIssuePanel';
import AllocationSummaryCard from './AllocationSummaryCard';

const TEMPLATE_TAB_KEY = 'voucherTemplate';
const isDealer = (role) => role === ROLE_INTERNAL_DEALER || role === ROLE_EXTERNAL_DEALER;

const LicenseDealerTab = ({
    user,
    role,
    selectedPackage,
    dealerAllocation,
    companies,
    allocationNodes,
    // FEAT-059: 제품(optional). 생략 = SMARTDent.
    product: productProp,
}) => {
    const dispatch = useDispatch();
    const product = useLicenseProduct(productProp);
    // 템플릿 편집기는 탭을 처음 열 때 마운트하고 이후 유지 — Self 탭 진입마다 템플릿 조회가 나가지 않게.
    const [templateVisited, setTemplateVisited] = useState(false);
    const handleInnerTabChange = useCallback((key) => {
        if (key === TEMPLATE_TAB_KEY) setTemplateVisited(true);
    }, []);
    const dealerExtraTabs = isDealer(role)
        ? [{ key: TEMPLATE_TAB_KEY, label: t('license.label.voucherTemplate'), content: templateVisited ? <VoucherTemplateEditor product={product} /> : null }]
        : [];

    const packageId = selectedPackage?._id;
    const companyId = user?.companyId;

    // Dealer: 자기 dealerId 로 직접 allocation 조회 → record.allocatedBy 에서 실제 corpId 획득.
    // parentCompanyId 와 실제 allocatedBy 가 다를 수 있어(예: 손자 dealer 에게 상위 법인이 직접 할당)
    // parentCompanyId 기반 추론은 부정확.
    const dealerAllocCorpId = isDealer(role) ? (dealerAllocation?.allocatedBy ?? null) : null;
    const corpId = isDealer(role) ? dealerAllocCorpId : companyId;

    // Subsidiary: 자기 예산(selfUsable) 표시용 노드 모델 서브트리 조회 (IMP-422 §12.1-B).
    useEffect(() => {
        if (role === ORG_SUBSIDIARY && packageId && companyId) {
            dispatch(SmartDentAction.GetAllocationSubtree(packageId, companyId, { product }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, role, packageId, companyId, product]);

    // FEAT-034 (2026-05-19): companies 로드 — role 가드 분리.
    // 외부 dealer voucher 가 PC 의 RAYTeams 계정에 등록된 케이스의 발급 회사명 표시 위해
    // companyMap 채우기 필수. BE 의 GET /company → getPermList 가 권한별 응답 (HQ → 전체,
    // 그 외 → cList) 처리하므로 모든 role 에서 호출 안전. companies empty 가드로 페이지당
    // 1회 호출 보장.
    //
    // HQ 사용자: 외부 dealer 회사명까지 lookup 가능 (전체 회사 응답).
    // Branch/Subsidiary/Dealer: cList 외 dealer 는 companyMap miss.
    // IMP-423 에서 해소 — BE `GET /voucher-license/meta` 가 dealerName 을 함께 반환하고
    // resolveOtherLicenseInfo 가 meta.dealerName > companyMap 순으로 사용한다.
    // 본 companyMap 은 DealerIssuePanel 의 나머지 회사명 표시에 계속 필요.
    //
    // PR #244 review S-2 (CQ Suggestion): deps [] — dispatch 는 stable, mount-once 의미.
    useEffect(() => {
        if (!companies || companies.length === 0) {
            dispatch(CompanyAction.GetCompany());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // companyId → company 매핑 — DealerIssuePanel 에 넘겨 회사명을 표시.
    // PR #244 review m-2 (Arch Minor): GetCompany() 가 모든 role 에서 호출되지만
    // Dealer 역할은 BE getPermList 가 cList 응답이라 본인 회사가 누락될 수 있음 →
    // user.companyInfo 보강 유지.
    const companyMap = useMemo(() => {
        const map = {};
        (companies || []).forEach((c) => { map[c.companyId] = c; });
        if (companyId && user?.companyInfo && !map[companyId]) {
            map[companyId] = user.companyInfo;
        }
        return map;
    }, [companies, companyId, user]);

    // Dealer 1단계: 자기 allocation record 조회 (corpId 미상 상태)
    useEffect(() => {
        if (!isDealer(role) || !companyId) return;
        dispatch(SmartDentAction.GetMyDealerAllocation(companyId, { product }));
    }, [dispatch, role, companyId, product]);

    // Subsidiary: 대상은 항상 자기 자신(self-dealing). 이전 선택 딜러의 dealerAllocation 이
    // Redux 에 남아 DealerIssuePanel 에 노출되지 않도록 null 로 초기화한 뒤 자기 레코드를 조회한다.
    const fetchSelfAllocation = useCallback(() => {
        if (!packageId || !companyId) return;
        dispatch({ type: SmartDentConstant.GET_DEALER_ALLOCATION, dealerAllocation: null, productKey: (product ?? DEFAULT_LICENSE_PRODUCT).key });
        dispatch(SmartDentAction.GetDealerAllocation(packageId, companyId, companyId, product));
    }, [dispatch, packageId, companyId, product]);
    useEffect(() => {
        if (role !== ORG_SUBSIDIARY) return;
        fetchSelfAllocation();
    }, [role, fetchSelfAllocation]);

    const remaining = computeDealerRemaining(dealerAllocation);

    // IMP-422 §12.1-B: 노드 모델(N단계) — 셀프딜링 레코드의 좁은 잔여(computeDealerRemaining) 대신
    // 법인 예산 selfUsable(개요/법인탭과 동일 값)을 표시.
    const parentOf = useMemo(() => buildParentOf(companies), [companies]);
    const hasAllocationNodes = !!allocationNodes && Object.keys(allocationNodes).length > 0;
    const nodeBreakdown = useMemo(
        () => computeNodeQuotaBreakdown(companyId, { nodesByCompanyId: allocationNodes, parentOf }),
        [companyId, allocationNodes, parentOf],
    );
    const useNodeModel = hasAllocationNodes && !!nodeBreakdown.effectiveOwner;
    const selfUsableRemaining = { newQty: nodeBreakdown.initial.selfUsable, renewQty: nodeBreakdown.renewal.selfUsable };

    // IMP-427 (#368): Allocation Summary 원천 수치 — 표시 remaining 과 같은 원천(노드 모델)에서 뽑는다
    // (레코드 t/u 와 노드 잔여가 섞이면 화면 수치가 어긋난다, #536). 딜러 자기-뷰는 dealerFacts.
    const dealerFacts = extractAllocationFacts(dealerAllocation);
    const selfOwner = nodeBreakdown.effectiveOwner;
    const selfNodeFacts = useNodeModel
        ? extractAllocationFacts(
            allocationNodes?.[selfOwner],
            getDirectAllocationChildren(selfOwner, allocationNodes),
        )
        : null;
    const subsidiaryFacts = selfNodeFacts || dealerFacts;
    const subsidiaryRemaining = useNodeModel ? selfUsableRemaining : remaining;
    // 라벨은 위 값 선택과 같은 분기: 노드 모델 = Self-Usable, 폴백 = 자기 레코드 t−u = Redeemable Remainder.
    const subsidiaryRemainingLabelKey = useNodeModel ? 'license.label.selfUsable' : 'license.label.redeemableRemainder';

    // IMP-392: Allocation Summary 리프레쉬 — dealer 는 자기 받은 할당 재조회.
    // BUG-211 R3: GetMyDealerAllocation 하나만 dispatch 하므로 fresh:true 로 매 클릭마다 실제 재조회.
    const fetchDealerSelfAllocation = useCallback(() => {
        if (!companyId) return;
        dispatch(SmartDentAction.GetMyDealerAllocation(companyId, { fresh: true, product }));
    }, [dispatch, companyId, product]);

    // Subsidiary 리프레쉬: 자기 예산 서브트리(fresh:true, BUG-211 캐시 우회) + 자기 레코드 재조회.
    const refreshSubsidiarySelf = useCallback(() => {
        if (!packageId || !companyId) return;
        dispatch(SmartDentAction.GetAllocationSubtree(packageId, companyId, { fresh: true, product }));
        fetchSelfAllocation();
    }, [dispatch, packageId, companyId, fetchSelfAllocation, product]);

    return (
        <div>
            {/* IMP-190 (2026-05-12): Voucher Template 노출은 isDealer(role) 만 허용.
                VoucherTemplateEditor 가 user.companyId 단일 (props 미수신) 로 동작하므로
                Subsidiary 가 Dealer 탭에서 편집해도 Subsidiary 탭과 동일 데이터를 편집한다.
                UX 혼란 방지 — Subsidiary 는 Subsidiary 탭의 Voucher Template 만 사용.
                PR #231 review (Suggestion): 본 컴포넌트는 단일 세션에서 role 불변을 전제
                (인증 변경 시 라우터 재마운트). 따라서 section state 의 role-기반 리셋
                useEffect 는 불요. */}
            {/* IMP-388: 구역 전환 버튼 줄을 없애고 DealerIssuePanel 의 안쪽 탭(extraTabs)으로 합쳤다.
                요약 띠는 DealerIssuePanel 의 summary 로 넘겨 PC 활성화·발행 버튼과 한 줄에 놓는다.
                안쪽 탭은 탭 줄 아래만 바꾸므로 템플릿 탭에서도 그 줄(요약 포함)은 그대로 보인다. */}
            <>
            {/* Subsidiary — "자기 발행" 전용(FR-005/006). 대상은 항상 자기 자신(self-dealing).
                딜러 선택·Allocate/Revoke 는 제거(관리/발급 현황 탭 담당). BUG-185: 대행 발행 금지 —
                canOperate 는 자기 자신이므로 항상 true. */}
            {role === ORG_SUBSIDIARY && companyId && (
                <div>
                    {/* SEC-006/BUG-200: self-issuance 는 DealerIssuePanel 내부 isSelfIssuance 로 통과.
                        companyMap 멤버십만 확인. */}
                    {companyMap[companyId] && (
                        <DealerIssuePanel
                            packageId={packageId}
                            corpId={companyId}
                            dealerId={companyId}
                            role={role}
                            companyMap={companyMap}
                            dealerAllocation={dealerAllocation}
                            product={product}
                            canOperate
                            // IMP-422 §12.1-B: 노드 모델이면 selfUsable 로 override.
                            remainingOverride={useNodeModel ? selfUsableRemaining : undefined}
                            summary={(
                                <AllocationSummaryCard
                                    remaining={subsidiaryRemaining}
                                    remainingLabelKey={subsidiaryRemainingLabelKey}
                                    testid="allocation-summary-card-subsidiary"
                                    onRefresh={refreshSubsidiarySelf}
                                    selfDealing
                                    total={subsidiaryFacts?.total}
                                    allocated={selfNodeFacts?.allocated}
                                    redeemed={subsidiaryFacts?.redeemed}
                                />
                            )}
                        />
                    )}
                </div>
            )}

            {/* 딜러 — 할당량 요약(summary) + PC 활성화 + 발행 버튼 + 발급 이력 + 고객 라이선스.
                RTSQA-339 round 16: Issue Voucher 는 DealerIssuePanel 내부(외부 버튼 없음). */}
            {isDealer(role) && (
                <DealerIssuePanel
                    packageId={packageId}
                    corpId={corpId}
                    dealerId={companyId}
                    role={role}
                    companyMap={companyMap}
                    dealerAllocation={dealerAllocation}
                    product={product}
                    // BUG-185: 딜러 자기-뷰는 항상 self-dealing — 발행/PC 활성화 허용.
                    canOperate
                    // IMP-190: 바우처 템플릿 탭은 딜러만(Subsidiary 는 Subsidiary 탭의 템플릿 사용).
                    extraTabs={dealerExtraTabs}
                    onTabChange={handleInnerTabChange}
                    summary={(
                        <AllocationSummaryCard
                            remaining={remaining}
                            testid="allocation-summary-card-dealer"
                            onRefresh={fetchDealerSelfAllocation}
                            // IMP-427: 딜러 자기-뷰 — dealer role 은 GetAllocationSubtree(위 useEffect, ORG_SUBSIDIARY 게이트)
                            // 를 호출하지 않아 allocationNodes 가 비어 Σ자식.t 를 알 수 없다 → 배분 배지 없음.
                            total={dealerFacts?.total}
                            redeemed={dealerFacts?.redeemed}
                        />
                    )}
                />
            )}
            </>
        </div>
    );
};

const mapState = (state, ownProps) => {
    const licenseState = selectLicenseProductState(state, ownProps?.product?.key);
    return {
        dealerAllocation: licenseState.dealerAllocation,
        companies: state.CompanyReducer.companies,
        allocationNodes: licenseState.allocationNodes,
    };
};

export default connect(mapState)(LicenseDealerTab);
